// src/lib/stripeplanselection.ts
import { supabase } from '../lib/supabase';

type SessionType = 'one-time' | 'subscription' | 'recurring';

/** 
 * נסיון לאחזר stripe_customer_id מה־DB כדי למנוע יצירת לקוח כפול ב־Stripe.
 * מחפש בטבלת subscriptions לפי business_id או user_id.
 */
async function tryGetStripeCustomerId(params: {
  business_id?: string;
  user_id?: string;
}): Promise<string | null> {
  try {
    // קודם לפי business_id (הכי חד-חד ערכי אצלך)
    if (params.business_id) {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('stripe_customer_id')
        .eq('business_id', params.business_id)
        .is('stripe_customer_id', null)
        .not('id', 'is', null) // noop to pass filters typecheck
        .limit(1);
      // הערה: הטריק עם .is(null) למעלה רק כדי להמחיש – נעדיף לשכתב לשתי בקשות:
    }

    if (params.business_id) {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('stripe_customer_id')
        .eq('business_id', params.business_id)
        .maybeSingle();

      if (!error && data?.stripe_customer_id) {
        return data.stripe_customer_id as string;
      }
    }

    // אם לא נמצא – ננסה לפי user_id
    if (params.user_id) {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('stripe_customer_id')
        .eq('user_id', params.user_id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && data?.stripe_customer_id) {
        return data.stripe_customer_id as string;
      }
    }
  } catch (_e) {
    // בולעים שגיאה – לא קריטי. ה־Edge Function תסתדר גם בלי customerId.
  }
  return null;
}

/**
 * יוצר סשן Stripe Checkout דרך Edge Function: CheckOutStripePlanSelection
 * שולח רק אחד: customerId או email (מונע השגיאה "You may only specify one of these parameters").
 */
export async function createCheckoutSession(
  priceId: string,
  type: SessionType,                          // השתמש ב-'subscription' לרישוי
  customerEmail?: string,
  metadata: Record<string, string> = {}
): Promise<string> {
  const projectUrl =
    (import.meta as any).env?.VITE_SUPABASE_URL ||
    'https://nkuqcyelxgyihrxyvitb.supabase.co';

  const baseUrl = `${projectUrl}/functions/v1/CheckOutStripePlanSelection`;

  // JWT אמיתי של המשתמש (לא anon key)
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userJwt = session?.access_token;

  // ניקוי metadata
  const cleanMeta: Record<string, string> = {};
  for (const [k, v] of Object.entries(metadata || {})) {
    if (typeof v === 'string' && v.trim().length > 0) cleanMeta[k] = v.trim();
  }

  const user_id = cleanMeta.user_id;
  const business_id = cleanMeta.business_id;

  if (!user_id || !business_id) {
    throw new Error('createCheckoutSession: missing user_id or business_id in metadata');
  }

  // המרה ל־mode שהפונקציה בשרת מצפה לו
  const mode = type === 'one-time' ? 'payment' : 'subscription';

  // ננסה להביא customerId מה־DB כדי לא ליצור לקוח חדש מיותר
  const existingCustomerId =
    cleanMeta.customerId || (await tryGetStripeCustomerId({ user_id, business_id }));

  // בונים גוף בקשה ל־Edge Function
  const body: any = {
    priceId,
    mode,                 // חשוב: השרת מצפה ל-'mode' ולא ל-'type'
    user_id,
    business_id,
    // נשלח email רק אם אין customerId
    ...(existingCustomerId
      ? { customerId: existingCustomerId }
      : customerEmail
      ? { email: customerEmail }
      : {}),
    // נשאיר גם ב-metadata להזדהות צולבת
    metadata: cleanMeta,
  };

  const res = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(userJwt ? { Authorization: `Bearer ${userJwt}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let errorMsg = `Failed to create checkout session: ${res.status} ${res.statusText}`;
    try {
      const errJson = await res.json();
      if (errJson?.error) errorMsg += ` - ${errJson.error}`;
    } catch {}
    throw new Error(errorMsg);
  }

  const data = await res.json().catch(() => {
    throw new Error('Response is not valid JSON');
  });

  if (!data?.url) {
    throw new Error('No checkout URL returned from server');
  }

  return data.url as string;
}

/**
 * שדרוג חבילה קיים (נשאר כמותאם לפונקציה PlanUpgrade – אם אתה משתמש בה).
 * אם תרצה לאחד ל־CheckOutStripePlanSelection בלבד, אמליץ לקרוא גם כאן לאותה פונקציה.
 */
export async function createCheckoutSessionUpgrade(
  priceId: string,
  type: SessionType = 'subscription',
  customerEmail?: string,
  metadata: Record<string, string> = {}
): Promise<string> {
  const {
    data: { session },
    error: sessionErr,
  } = await supabase.auth.getSession();

  if (sessionErr) throw new Error(`Auth error: ${sessionErr.message}`);
  if (!session) throw new Error('Not authenticated – אנא התחבר/י מחדש');

  // אם יש לך Edge Function נפרדת לשדרוג:
  const { data, error } = await supabase.functions.invoke('PlanUpgrade', {
    body: {
      priceId,
      type,
      customerEmail,
      metadata,
    },
  });

  if (error) {
    const status = (error as any)?.status ?? 'unknown';
    const msg = error.message || 'Unknown error';
    throw new Error(`Failed to create checkout session: ${status} - ${msg}`);
  }

  const url = (data as any)?.url as string | undefined;
  if (!url) throw new Error('No checkout URL returned from PlanUpgrade');

  return url;
}
